import React, { useMemo, useState } from "react";

import PersonAddIcon from "@mui/icons-material/PersonAdd";
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { makeStyles, withStyles } from "tss-react/mui";

import SearchIcon from "@/components/Icons/SearchIcon";
import { graphql } from "@/gql";
import { useFragment } from "@/hooks/useFragment";
import useSearchText from "@/hooks/useSearchText";
import { colors } from "@/theme/overrides/colors";
import { formatISODate } from "@/utils/datetime";

import ActiveClientCard from "./ActiveClientCard";
import StrugglingClientCard from "./StrugglingClientCard";
import NewClientDialog from "../../../Clients/ClientList/components/NewClientDialog";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface Props {
  loading?: boolean;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const StyledTabs = withStyles(Tabs, {
  indicator: {
    display: "flex",
    justifyContent: "center",
    backgroundColor: "transparent",
    "& > span": {
      maxWidth: 60,
      width: "100%",
      backgroundColor: colors.primary,
    },
  },
});

const StyledTab = withStyles(Tab, {
  root: {
    color: colors.grey,
    "&.Mui-selected": {
      color: colors.primary,
    },
  },
});

const StyledTextField = withStyles(TextField, (theme) => ({
  root: {
    width: theme.typography.pxToRem(360),
  },
}));

const StyledButton = withStyles(Button, (theme) => ({
  contained: {
    height: theme.typography.pxToRem(32),
  },
}));

const useStyles = makeStyles()(() => ({
  searchBar: {
    height: 8,
  },
}));

export const activeAndStrugglingClientsFragment = graphql(`
  fragment ActiveAndStrugglingClients on Query {
    activeClients {
      nodes {
        ...ActiveClientCard
      }
    }

    strugglingClients {
      nodes {
        ...StrugglingClientCard
      }
    }
  }
`);

const ActiveAndStrugglingClients: React.FC<Props> = ({ loading = false }) => {
  const { classes } = useStyles();
  const [searchText, setSearchText] = useSearchText({ debounce: 500 });
  const [tabValue, setTabValue] = useState<number>(0);

  const [data] = useFragment(activeAndStrugglingClientsFragment, "ROOT_QUERY", {
    variables: { currentDate: formatISODate(new Date()) },
  });

  const [isNewClientDialogOpen, setIsNewClientDialogOpen] =
    useState<boolean>(false);
  const openNewClientDialog = () => setIsNewClientDialogOpen(true);
  const closeNewClientDialog = () => setIsNewClientDialogOpen(false);

  const activeClients = useMemo(() => {
    return (
      data?.activeClients.nodes.filter((client) =>
        client.profile?.fullName.toLowerCase().includes(searchText)
      ) ?? []
    );
  }, [data?.activeClients, searchText]);
  const strugglingClients = useMemo(() => {
    return (
      data?.strugglingClients.nodes.filter((client) =>
        client.profile?.fullName.toLowerCase().includes(searchText)
      ) ?? []
    );
  }, [data?.strugglingClients, searchText]);

  const activeClientsCount = data?.activeClients.nodes.length ?? 0;
  const strugglingClientsCount = data?.strugglingClients.nodes.length ?? 0;

  function getActiveClients() {
    if (loading && data == null) {
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          padding={14}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (activeClients.length > 0) {
      return activeClients.map((client) => (
        <ActiveClientCard key={client.id} id={client.id} />
      ));
    } else {
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          padding={14}
        >
          <Typography variant="h6" color="textSecondary">
            No Active Clients
          </Typography>
        </Box>
      );
    }
  }

  function getStrugglingClients() {
    if (loading && data == null) {
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          padding={14}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (strugglingClients.length > 0) {
      return strugglingClients?.map((client) => (
        <StrugglingClientCard key={client.id} id={client.id} />
      ));
    } else {
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          padding={14}
        >
          <Typography variant="h6" color="textSecondary">
            No Struggling Clients
          </Typography>
        </Box>
      );
    }
  }
  return (
    <>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        padding={1}
        marginTop={4}
        marginLeft={4}
        style={{ backgroundColor: colors.white }}
      >
        <StyledTabs
          TabIndicatorProps={{ children: <span /> }}
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
        >
          <StyledTab label="Active Clients" {...a11yProps(0)} />
          <StyledTab label="Struggling Clients" {...a11yProps(1)} />
        </StyledTabs>
        <StyledButton
          variant="contained"
          color="primary"
          startIcon={<PersonAddIcon />}
          onClick={openNewClientDialog}
        >
          Add New Client
        </StyledButton>
      </Box>

      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        marginLeft={4}
        marginTop={4}
        justifyContent="space-between"
      >
        {tabValue == 0 ? (
          <Typography variant="h6" color="textPrimary">
            Active clients ({activeClientsCount})
          </Typography>
        ) : (
          <Typography variant="h6" color="textPrimary">
            Struggling clients ({strugglingClientsCount})
          </Typography>
        )}
        <StyledTextField
          placeholder="Search..."
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            classes: { input: classes.searchBar },
          }}
          variant="outlined"
        />
      </Box>

      <TabPanel value={tabValue} index={0}>
        {getActiveClients()}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {getStrugglingClients()}
      </TabPanel>

      <NewClientDialog
        open={isNewClientDialogOpen}
        onClose={closeNewClientDialog}
        afterCreate={async () => {
          closeNewClientDialog();
        }}
      />
    </>
  );
};

export default ActiveAndStrugglingClients;
